// ============================================================
// TMR Portal Admin — filtrar a lista de Empresas por gestor
// ============================================================
import { useState } from 'react';
import { RecordPicker } from '../../components/RecordPicker';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useCompanyManagerSearch } from '../../data/companies/queries';
import type { CompanyManagerOption } from '../../data/companies/contract';

/** What the filter is currently narrowed to: a chosen colleague, or nobody in particular. */
export interface ManagerFilterValue {
  /** The User id sent to the backend, or empty for "todos". */
  userId: string;
  /** Their name, so the control can say who is picked without a second lookup. */
  name: string;
}

/** Nothing chosen — the list shows every manager's Empresas. */
export const ALL_MANAGERS: ManagerFilterValue = { userId: '', name: '' };

/** Props for {@link ManagerFilter}. */
interface ManagerFilterProps {
  /** Field label, e.g. "Gestor de venda". */
  label: string;
  /** One line in the picker explaining what choosing somebody does. */
  subtitle: string;
  value: ManagerFilterValue;
  onChange: (value: ManagerFilterValue) => void;
  /** The current operator, so "Eu" needs no round-trip. */
  me: ManagerFilterValue;
}

/**
 * Narrows the Empresas list to one colleague's carteira (18 §9).
 *
 * It is a **filter, not a scope**: it changes which Empresas are listed, never which ones the operator is
 * allowed to see — the backend applies the authorization scope on top, unchanged (06 §6).
 *
 * The "Eu" shortcut is the reason this is not a plain picker. Isolating your own carteira is the thing
 * people do many times a day, and making that two clicks through a search box for a name you already know
 * is the difference between a filter that gets used and one that does not.
 */
export function ManagerFilter({ label, subtitle, value, onChange, me }: ManagerFilterProps) {
  const [picking, setPicking] = useState(false);
  const isMe = value.userId === me.userId;

  return (
    <div>
      <label className="pa-form-label">{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="pa-input"
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 168, textAlign: 'left', cursor: 'pointer' }}
          title={value.userId ? `Filtrando por ${value.name}` : 'Todos os gestores'}
        >
          <StrokeIcon d={Icons.users} size={14} />
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value.userId ? value.name : 'Todos'}
          </span>
        </button>

        {/* Shown only when it would change something: pressing "Eu" while already filtered to yourself
            does nothing, and a button that does nothing is a button that wastes a click. */}
        {!isMe && (
          <button type="button" onClick={() => onChange(me)} className="pa-link-btn" style={{ fontSize: 12.5 }}>
            Eu
          </button>
        )}

        {value.userId && (
          <button
            type="button"
            onClick={() => onChange(ALL_MANAGERS)}
            className="pa-icon-btn"
            style={{ width: 28, height: 28 }}
            title="Limpar filtro"
          >
            <StrokeIcon d={Icons.x} size={15} />
          </button>
        )}
      </div>

      {picking && (
        <RecordPicker<CompanyManagerOption>
          eyebrow={label}
          title="Filtrar por gestor"
          subtitle={subtitle}
          placeholder="Nome ou e-mail do colaborador…"
          useSearch={useCompanyManagerSearch}
          getKey={(user) => user.id}
          toRow={(user) => ({ primary: user.name, secondary: user.email })}
          onSelect={(user) => onChange({ userId: user.id, name: user.name })}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
